import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/internal/operators/map';
import { shareReplay, tap, catchError } from 'rxjs/operators';
import { Rescatable } from '@core/models/Rescatable';
import { HttpClient } from '@angular/common/http';
import { HandleErrorService } from './handle-error.service';
import { PiramidalHookService, relation } from './piramidalHook.service';
import { SocialLoginService } from './social-login.service';

export type ObjetoPiramidal<T> = {
  observable: Observable<T[]>;
  cache: { [index: number]: T };
  getByIndex: (indexes: number[], forzar?: boolean) => Observable<T[]>;
};

@Injectable({
  providedIn: 'root',
})
export class PiramidalCacheService {
  [x: string]: any;
  private caches: {
    [x: string]: {
      type: new (o: Rescatable) => Rescatable;
      pathRequest: string;
      pathRequestIndex?: string;
      pk: string;
      idFacebookSignedRequest?: boolean;
      observable?: Observable<any[]>;
      observableIndex?: (indexes: number[], forzar: boolean) => Observable<any[]>;
      status: boolean;
      objetoPiramidal?: ObjetoPiramidal<any>;
      index: {
        [index: number]: Rescatable;
      };
    };
  } = {};
  private idFacebookSinged: string;
  constructor(
    private http: HttpClient,
    private piramidalHook: PiramidalHookService,
    private handleError: HandleErrorService,
    private socialLogin: SocialLoginService, //social
  ) {
    this.socialLogin.observable.subscribe((token) => {
      this.idFacebookSinged = token;
      Object.keys(this.caches).forEach((cache) => {
        this.caches[cache].objetoPiramidal.observable = this.makeObservable(cache);
      });
    });
  }

  add<T extends Rescatable>(classRef: new (o: T) => T): ObjetoPiramidal<T> {
    const tname = (classRef.prototype.constructor as typeof Rescatable)._name;
    this.caches[tname] = {
      type: classRef,
      pathRequest: (classRef.prototype.constructor as typeof Rescatable).pathRequest,
      pathRequestIndex: (classRef.prototype.constructor as typeof Rescatable).pathRequestIndex,
      pk: (classRef.prototype.constructor as typeof Rescatable).pk,
      idFacebookSignedRequest: (classRef.prototype.constructor as typeof Rescatable)
        .idFacebookSignedRequest,
      status: false,
      index: {},
    };
    return this.getObservable(classRef);
  }

  private makeObservable(cache: string) {
    const opciones: { observe?: 'body'; params?: any } = { observe: 'body' };
    if (this.idFacebookSinged && this.caches[cache].idFacebookSignedRequest) {
      opciones.params = {
        facebook_signedRequest: this.idFacebookSinged,
      };
    }
    return (this.caches[cache].observable = this.http
      .get<any[]>(this.caches[cache].pathRequest, opciones)
      .pipe(
        map((array) => {
          return array.map((e) => {
            const newE = new this.caches[cache].type(e);
            if (this.caches[cache].index[newE[this.caches[cache].pk]]) {
              this.caches[cache].index[newE[this.caches[cache].pk]] = {
                ...this.caches[cache].index[newE[this.caches[cache].pk]],
                ...newE,
              };
            } else {
              this.caches[cache].index[newE[this.caches[cache].pk]] = newE;
            }
            return newE;
          });
        }),
        tap((arrayT) => {
          const hook: relation<unknown, unknown>[] = this.piramidalHook.indexes[cache];
          this.caches[cache].status = true;
          if (hook) {
            hook.forEach((relation) => {
              this.hook(cache, relation, arrayT);
            });
          }
        }),
        shareReplay(1),
        catchError(this.handleError.handleError),
      ));
  }
  private getObservable<T extends Rescatable>(classRef: new (o: T) => T): ObjetoPiramidal<T> {
    const cache = (classRef.prototype.constructor as typeof Rescatable)._name;
    if (!this.caches[cache].observable) {
      this.caches[cache].observable = this.makeObservable(cache);
    }

    if (!this.caches[cache].observableIndex) {
      if (!this.caches[cache].pathRequestIndex) {
        this.caches[cache].observableIndex = (indexes: any[], forzar: boolean = false) => {
          return Observable.create((observer) => {
            this.caches[cache].observable.subscribe(() => {
              observer.next(
                indexes.map((index) => {
                  return this.caches[cache].index[index];
                }),
              );
            });
          });
        };
      } else {
        this.caches[cache].observableIndex = (indexes, forzar: boolean) => {
          const indexesFinal = indexes.filter((index) => {
            return !this.caches[cache].index[index] || forzar;
          });
          const resCache = indexes
            .filter((index) => {
              return this.caches[cache].index[index];
            })
            .map((index) => {
              return this.caches[cache].index[index];
            });
          if (!forzar && !indexesFinal.length) {
            return Observable.create((observer) => {
              observer.next(resCache);
            });
          }

          let opciones: { observe?: 'body'; params: any } = {
            observe: 'body',
            params: { ids: indexesFinal },
          };
          if (this.socialLogin.idFacebookFirmado) {
            opciones.params.facebook_signedRequest = this.socialLogin.idFacebookFirmado;
          }
          let endpoint = this.caches[cache].pathRequestIndex;
          if (this.caches[cache].pathRequestIndex.includes('static') && indexesFinal) {
            endpoint += `/${indexesFinal}`;
            opciones = { observe: 'body', params: null };
          }

          return this.http.get<T[]>(endpoint, opciones).pipe(
            map((array) => {
              let arr = array;
              if (!Array.isArray(array)) {
                arr = [array];
              }
              const elements = arr?.map((e) => {
                const newE = new this.caches[cache].type(e);
                if (this.caches[cache].index[newE[this.caches[cache].pk]]) {
                  this.caches[cache].index[newE[this.caches[cache].pk]] = {
                    ...this.caches[cache].index[newE[this.caches[cache].pk]],
                    ...newE,
                  };
                } else {
                  this.caches[cache].index[newE[this.caches[cache].pk]] = newE;
                }
                return newE;
              });
              elements.concat(resCache);
              return elements;
            }),
            map((arrayT) => {
              const valores = arrayT.map((e) => {
                return this.caches[cache].index[e[this.caches[cache].pk]];
              });
              return valores;
            }),
            tap((arrayT) => {
              const hook: relation<unknown, unknown>[] = this.piramidalHook.indexes[cache];
              this.caches[cache].status = true;
              if (hook) {
                hook.forEach((relation) => {
                  this.hook(cache, relation, arrayT);
                });
              }
            }),
            map((arrayT) => {
              const valores = arrayT.map((e) => {
                return this.caches[cache].index[e[this.caches[cache].pk]];
              });
              return valores;
            }),
            shareReplay(1),
          );
        };
      }
    }
    this.caches[cache].objetoPiramidal = {
      observable: this.caches[cache].observable,
      getByIndex: this.caches[cache].observableIndex,
      cache: this.caches[cache].index,
    };
    return this.caches[cache].objetoPiramidal;
  }

  private hook(nameCache, relation: relation<Rescatable, Rescatable>, values, vuelta = false) {
    let directo = nameCache == relation.class._name;

    if (directo && vuelta && relation.class._name == relation.foreinClass._name) {
      directo = false;
    }

    let classRelation = relation.foreinClass;
    if (!directo) {
      classRelation = relation.class;
    }
    if (!this.caches[classRelation._name].status) {
      return;
    }
    const fieldInValue = directo ? relation.ownFieldIndex : relation.foreinFieldIndex;
    const fieldDestiny = directo ? relation.ownField : relation.foreinField;
    const fieldInForain = directo ? relation.foreinFieldIndex : relation.ownFieldIndex;

    const cacheNameForain = directo ? relation.foreinClass._name : relation.class._name;

    const cacheNameOwn = directo ? relation.class._name : relation.foreinClass._name;

    const cacheForain = this.caches[cacheNameForain];
    const cacheOwn = this.caches[cacheNameOwn];

    const isIndex = cacheForain.pk == fieldInForain;

    values.forEach((e: any) => {
      if (e[fieldInValue]) {
        const valorPropio = e[fieldInValue];
        if (isIndex) {
          e[fieldDestiny] = this.caches[cacheNameForain].index[valorPropio];
        } else {
          if (!e[fieldDestiny]) {
            e[fieldDestiny] = [];
          }

          Object.keys(cacheForain.index).forEach((key) => {
            const forainElement = cacheForain.index[key];
            if (forainElement[fieldInForain] == valorPropio) {
              const pksElements: number[] = e[fieldDestiny].map(
                (element) => element[cacheForain.pk],
              );
              if (!pksElements.includes(forainElement[cacheForain.pk])) {
                e[fieldDestiny].push(this.caches[cacheNameForain].index[key]);
              } else {
                for (let i = 0; i < e[fieldDestiny].length; i++) {
                  e[fieldDestiny][i] = this.caches[cacheNameForain].index[key];
                }
              }
            }
          });
        }
      }
    });
    if (!vuelta) {
      this.hook(classRelation._name, relation, Object.values(cacheForain.index), true);
    }
  }
}
